import React, { useEffect, useState } from "react";
import axios from "../api/axiosInstance";
import {
  FaTrash,
  FaSearch,
  FaEnvelope,
  FaPhone,
  FaInbox,
} from "react-icons/fa";

export default function ContactUsTable() {
  const [contacts, setContacts] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(
    () => parseInt(localStorage.getItem("pageSize")) || 10,
  );
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Fetch ContactUs Data
  const fetchContacts = async () => {
    setLoading(true);
    try {
      const params = { search: filterText, page, page_size: pageSize };
      const res = await axios.get("/api/contact-us-admin/", { params });
      setContacts(res.data.results || res.data);
      if (res.data.count !== undefined) setCount(res.data.count);
    } catch (err) {
      console.error("Error fetching contact data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterText, page, pageSize]);

  const totalPages = Math.ceil(count / pageSize) || 1;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this enquiry?")) {
      setDeletingId(id);
      try {
        await axios.delete(`/api/contact-us-admin/${id}/`);
        fetchContacts();
      } catch (err) {
        console.error("Error deleting contact:", err);
      } finally {
        setDeletingId(null);
      }
    }
  };

  // Build a compact, smart pagination range (with ellipses) so it never
  // breaks on small screens even with many pages.
  const getPageNumbers = () => {
    const pages = [];
    const delta = 1;
    const range = [];

    for (
      let i = Math.max(2, page - delta);
      i <= Math.min(totalPages - 1, page + delta);
      i++
    ) {
      range.push(i);
    }

    if (page - delta > 2) range.unshift("ellipsis-start");
    if (page + delta < totalPages - 1) range.push("ellipsis-end");

    pages.push(1);
    pages.push(...range);
    if (totalPages > 1) pages.push(totalPages);

    return [...new Set(pages)];
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="px-3 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-1">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-sm">
              <FaInbox size={16} />
            </span>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
                Contact Enquiries
              </h1>
              <p className="text-sm text-slate-500">
                {count} {count === 1 ? "enquiry" : "enquiries"} received
              </p>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-sm">
            <FaSearch
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              size={14}
            />
            <input
              type="text"
              placeholder="Search by name, email or phone..."
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-4 text-sm text-slate-800 placeholder:text-slate-400 transition focus:border-indigo-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100"
              value={filterText}
              onChange={(e) => {
                setPage(1);
                setFilterText(e.target.value);
              }}
            />
          </div>

          <div className="flex items-center justify-between gap-2 sm:justify-end">
            <label
              htmlFor="pageSize"
              className="whitespace-nowrap text-sm font-medium text-slate-600"
            >
              Rows per page
            </label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={(e) => {
                setPage(1);
                setPageSize(Number(e.target.value));
                localStorage.setItem("pageSize", e.target.value);
              }}
              className="cursor-pointer rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700 transition focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        {/* ===== Desktop / tablet table ===== */}
        <div className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm md:block">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/80 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-5 py-3.5">Name</th>
                  <th className="px-5 py-3.5">Contact</th>
                  <th className="px-5 py-3.5">Enquiry</th>
                  <th className="px-5 py-3.5">Received</th>
                  <th className="px-5 py-3.5 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-5 py-12 text-center text-slate-400"
                    >
                      <span className="inline-flex items-center gap-2 text-sm">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-500" />
                        Loading enquiries...
                      </span>
                    </td>
                  </tr>
                ) : contacts.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-5 py-14 text-center">
                      <div className="flex flex-col items-center gap-2 text-slate-400">
                        <FaInbox size={28} />
                        <p className="text-sm font-medium text-slate-500">
                          No enquiries found
                        </p>
                        <p className="text-xs">
                          Try adjusting your search or check back later
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  contacts.map((c) => (
                    <tr
                      key={c.id}
                      className="group transition-colors hover:bg-indigo-50/40"
                    >
                      <td className="px-5 py-4 align-top">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-700">
                            {c.name ? c.name.charAt(0).toUpperCase() : "?"}
                          </span>
                          <span className="font-medium text-slate-800">
                            {c.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4 align-top">
                        <div className="flex flex-col gap-1 text-slate-600">
                          <span className="flex items-center gap-1.5">
                            <FaEnvelope size={11} className="text-slate-400" />
                            <span className="truncate">{c.email}</span>
                          </span>
                          <span className="flex items-center gap-1.5">
                            <FaPhone size={11} className="text-slate-400" />
                            {c.phone_number}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4 align-top">
                        <p className="max-w-xs text-slate-600 line-clamp-2">
                          {c.enquiry}
                        </p>
                      </td>
                      <td className="px-5 py-4 align-top text-slate-500">
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-700">
                            {formatDate(c.created_at)}
                          </span>
                          <span className="text-xs">
                            {formatTime(c.created_at)}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-center align-top">
                        <button
                          onClick={() => handleDelete(c.id)}
                          disabled={deletingId === c.id}
                          aria-label={`Delete enquiry from ${c.name}`}
                          className="rounded-full p-2.5 text-slate-400 transition hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                        >
                          {deletingId === c.id ? (
                            <span className="block h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-red-500" />
                          ) : (
                            <FaTrash size={14} />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ===== Mobile cards ===== */}
        <div className="space-y-3 md:hidden">
          {loading ? (
            <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white py-12 text-slate-400 shadow-sm">
              <span className="inline-flex items-center gap-2 text-sm">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-500" />
                Loading enquiries...
              </span>
            </div>
          ) : contacts.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-white py-14 text-slate-400 shadow-sm">
              <FaInbox size={28} />
              <p className="text-sm font-medium text-slate-500">
                No enquiries found
              </p>
              <p className="text-xs">Try adjusting your search</p>
            </div>
          ) : (
            contacts.map((c) => (
              <div
                key={c.id}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-700">
                      {c.name ? c.name.charAt(0).toUpperCase() : "?"}
                    </span>
                    <div>
                      <p className="font-medium text-slate-800">{c.name}</p>
                      <p className="text-xs text-slate-400">
                        {formatDate(c.created_at)} &middot;{" "}
                        {formatTime(c.created_at)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(c.id)}
                    disabled={deletingId === c.id}
                    aria-label={`Delete enquiry from ${c.name}`}
                    className="shrink-0 rounded-full p-2.5 text-slate-400 transition hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                  >
                    {deletingId === c.id ? (
                      <span className="block h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-red-500" />
                    ) : (
                      <FaTrash size={14} />
                    )}
                  </button>
                </div>

                <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-3 text-sm text-slate-600">
                  <p className="flex items-center gap-2">
                    <FaEnvelope size={11} className="shrink-0 text-slate-400" />
                    <span className="truncate">{c.email}</span>
                  </p>
                  <p className="flex items-center gap-2">
                    <FaPhone size={11} className="shrink-0 text-slate-400" />
                    {c.phone_number}
                  </p>
                </div>

                <p className="mt-3 rounded-xl bg-slate-50 p-3 text-sm leading-relaxed text-slate-600">
                  {c.enquiry}
                </p>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {!loading && contacts.length > 0 && (
          <div className="mt-5 flex flex-col items-center justify-between gap-3 sm:flex-row">
            <p className="text-sm text-slate-500">
              Page <span className="font-medium text-slate-700">{page}</span> of{" "}
              <span className="font-medium text-slate-700">{totalPages}</span>
            </p>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Prev
              </button>

              <div className="flex items-center gap-1">
                {getPageNumbers().map((p, i) =>
                  typeof p === "string" ? (
                    <span
                      key={p + i}
                      className="px-1.5 text-sm text-slate-400 select-none"
                    >
                      &hellip;
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => handlePageChange(p)}
                      className={`min-w-[2.25rem] rounded-lg px-2.5 py-1.5 text-sm font-medium transition ${
                        page === p
                          ? "bg-indigo-600 text-white shadow-sm"
                          : "text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      {p}
                    </button>
                  ),
                )}
              </div>

              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
